import { formatCurrency, formatIsoDate } from '@if/core/utils'
import { Eyebrow, MetaValue } from '@if/mui'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import TableCellNoWrap from '@src/components/Common/Table/TableCellNoWrap'
import { getTimeNow } from '@src/services/DateTimeProvider'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { getColorForDifference, useLoanCalendarActionPreviewData } from './LoanCalendarActionPreview-hook'

const LoanCalendarActionPreviewTableHeader = memo(({ title }: { title: string }) => {
  const { t } = useTranslation()
  return (
    <TableRow>
      <TableCell>
        <Eyebrow>{title}</Eyebrow>
      </TableCell>
      <TableCell>
        <Eyebrow>{t('common.before')}</Eyebrow>
      </TableCell>
      <TableCell>
        <Eyebrow>{t('common.after')}</Eyebrow>
      </TableCell>
      <TableCell />
    </TableRow>
  )
})
LoanCalendarActionPreviewTableHeader.displayName = 'LoanCalendarActionPreviewTableHeader'

interface LoanCalendarActionPreviewTableProps {
  simulationBefore: CalendarSimulation
  simulationAfter: CalendarSimulation
  actionDate?: Date
}

const LoanCalendarActionPreviewTable = ({
  simulationBefore,
  simulationAfter,
  actionDate,
}: LoanCalendarActionPreviewTableProps) => {
  const { t } = useTranslation()
  const beforeOp = simulationBefore.summaryInformation
  const afterOp = simulationAfter.summaryInformation
  const {
    lastPaymentDateBefore,
    lastPaymentDateAfter,
    lastPaymentDateDifference,
    lastTotalInterestPaidBefore,
    lastTotalInterestPaidAfter,
    totalInterestPaidDifference,
  } = useLoanCalendarActionPreviewData(simulationBefore, simulationAfter, actionDate)

  return (
    <Table size="small">
      <TableBody>
        <LoanCalendarActionPreviewTableHeader
          title={t('loan.payment.preview.projectedDelay', {
            date: formatIsoDate(getTimeNow()),
          })}
        />
        <TableRow>
          <TableCell>{t('loan.payment.preview.nbLateDays')}</TableCell>
          <TableCellNoWrap>{t('common.days', { count: beforeOp?.daysLate || 0 })}</TableCellNoWrap>
          <TableCellNoWrap>
            <MetaValue>{t('common.days', { count: afterOp?.daysLate || 0 })}</MetaValue>
          </TableCellNoWrap>
          <TableCell />
        </TableRow>
        <LoanCalendarActionPreviewTableHeader title={t('loan.payment.preview.paymentsEnd')} />
        <TableRow>
          <TableCell>{t('loan.payment.preview.lastPayment')}</TableCell>
          <TableCellNoWrap>{formatIsoDate(lastPaymentDateBefore)}</TableCellNoWrap>
          <TableCellNoWrap>
            <MetaValue color={getColorForDifference(lastPaymentDateBefore.getTime(), lastPaymentDateAfter.getTime())}>
              {formatIsoDate(lastPaymentDateAfter)}
            </MetaValue>
          </TableCellNoWrap>
          <TableCellNoWrap>{lastPaymentDateDifference}</TableCellNoWrap>
        </TableRow>
        <TableRow>
          <TableCell>{t('loan.payment.preview.totalInterestPaid')}</TableCell>
          <TableCellNoWrap>{formatCurrency(lastTotalInterestPaidBefore)}</TableCellNoWrap>
          <TableCellNoWrap>
            <MetaValue color={getColorForDifference(lastTotalInterestPaidBefore, lastTotalInterestPaidAfter)}>
              {formatCurrency(lastTotalInterestPaidAfter)}
            </MetaValue>
          </TableCellNoWrap>
          <TableCellNoWrap>
            <MetaValue color={getColorForDifference(0, totalInterestPaidDifference)}>
              {formatCurrency(totalInterestPaidDifference)}
            </MetaValue>
          </TableCellNoWrap>
        </TableRow>
      </TableBody>
    </Table>
  )
}

export default memo(LoanCalendarActionPreviewTable)
